import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { syncService } from '../services/syncService';
import { useSyncStore } from '../store/syncStore';
import { cachePerformanceMonitor } from '../utils/cachePerformance';
import { securityAuditLogger } from '../utils/securityAuditLogger';
import { tokenManager } from '../utils/tokenManager';
import { validateConnection, logConnectionGuardStatus } from '../utils/connectionGuard';
import { sessionMonitor } from '../services/sessionMonitor';
import type { QueuedAction } from '../store/syncStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Retry configuration for transient network errors
 */
const RETRY_CONFIG = {
  /** Maximum number of retry attempts */
  MAX_RETRIES: 3,
  
  /** Initial retry delay in milliseconds */
  INITIAL_DELAY: 1000,
  
  /** Multiplier for exponential backoff */
  BACKOFF_MULTIPLIER: 2,
  
  /** Maximum retry delay in milliseconds */
  MAX_DELAY: 10000,
  
  /** HTTP status codes that should trigger retry */
  RETRYABLE_STATUS_CODES: [408, 429, 500, 502, 503, 504],
  
  /** Error messages that indicate transient failures */
  RETRYABLE_ERROR_MESSAGES: ['Network Error', 'timeout', 'ECONNABORTED', 'ETIMEDOUT']
};

/**
 * Check if error is retryable
 * 
 * @param error - Axios error object
 * @returns True if error should be retried
 */
function isRetryableError(error: AxiosError): boolean {
  // Check for retryable status codes
  if (error.response?.status && RETRY_CONFIG.RETRYABLE_STATUS_CODES.includes(error.response.status)) {
    return true;
  }
  
  // Check for retryable error messages
  if (error.message) {
    return RETRY_CONFIG.RETRYABLE_ERROR_MESSAGES.some(msg => 
      error.message.toLowerCase().includes(msg.toLowerCase())
    );
  }
  
  return false;
}

/**
 * Calculate retry delay with exponential backoff
 * 
 * @param retryCount - Current retry attempt number (0-indexed)
 * @returns Delay in milliseconds
 */
function getRetryDelay(retryCount: number): number {
  const delay = RETRY_CONFIG.INITIAL_DELAY * Math.pow(RETRY_CONFIG.BACKOFF_MULTIPLIER, retryCount);
  return Math.min(delay, RETRY_CONFIG.MAX_DELAY);
}

/**
 * Sleep for specified duration
 * 
 * @param ms - Duration in milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Check if error is a SESSION_KICKED error
 * 
 * @param error - Axios error object
 * @returns True if error indicates session was kicked
 */
export function isSessionKickedError(error: any): boolean {
  return (
    error.response?.status === 401 &&
    error.response?.data?.error === 'SESSION_KICKED'
  );
}

/**
 * Get user-friendly error message for SESSION_KICKED errors
 * 
 * @param error - Axios error object
 * @returns User-friendly error message
 */
export function getSessionKickedMessage(error: any): string {
  const defaultMessage = 'Your session has been invalidated. Please log in again.';
  
  if (!isSessionKickedError(error)) {
    return defaultMessage;
  }
  
  const errorData = error.response?.data;
  const message = errorData?.message || defaultMessage;
  const details = errorData?.details;
  
  if (details) {
    return `${message} (${details})`;
  }
  
  return message;
}

// Log connection guard status on module load
logConnectionGuardStatus();

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add JWT token, handle token refresh, and performance monitoring
apiClient.interceptors.request.use(
  async (config) => {
    // Debug log for all requests
    console.log('[API Client] Request:', {
      method: config.method,
      url: config.url,
      params: config.params,
      baseURL: config.baseURL
    });
    
    // Validate connection in development (blocks external connections)
    const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
    if (fullUrl) {
      validateConnection(fullUrl);
    }
    
    // Check if token needs refresh before making request
    const token = await tokenManager.getValidToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      
      // Log security event for sensitive operations
      if (config.method !== 'get') {
        securityAuditLogger.logSecurityEvent('api_request', {
          method: config.method?.toUpperCase(),
          url: config.url,
          hasAuth: true,
          timestamp: new Date().toISOString()
        });
      }
    } else if (config.url && !config.url.includes('/auth/')) {
      // No valid token for protected endpoint
      securityAuditLogger.logSecurityEvent('unauthorized_request', {
        method: config.method?.toUpperCase(),
        url: config.url,
        hasAuth: false,
        timestamp: new Date().toISOString()
      });
    }
    
    // Add user timezone header for timezone-aware API responses
    const savedTimezone = localStorage.getItem('user_timezone_preference');
    const userTimezone = savedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (userTimezone) {
      config.headers['X-User-Timezone'] = userTimezone;
    }
    
    // Add cache control headers for better caching
    if (config.method === 'get') {
      // Let browser handle caching naturally
      config.headers['Cache-Control'] = 'max-age=300'; // 5 minutes default
    }
    
    // Record request start time for performance monitoring
    config.metadata = { startTime: Date.now() };
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Track if we're currently refreshing token to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Response interceptor - handle 401 errors with token refresh, offline scenarios, retry logic, and performance monitoring
apiClient.interceptors.response.use(
  (response) => {
    // Record performance metrics
    const startTime = response.config.metadata?.startTime;
    if (startTime) {
      const responseTime = Date.now() - startTime;
      const cacheHit = response.status === 304 || 
                       response.headers['x-cache'] === 'HIT' ||
                       responseTime < 50; // Very fast responses likely from cache
      
      cachePerformanceMonitor.recordFetchRequest(
        response.config.url || '',
        response.config.method?.toUpperCase() || 'GET',
        startTime,
        cacheHit,
        parseInt(response.headers['content-length'] || '0')
      );
    }
    
    // Store ETag for future conditional requests
    if (response.headers.etag) {
      localStorage.setItem(`etag_${response.config.url}`, response.headers.etag);
    }
    
    return response.data;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { 
      _retry?: boolean;
      _retryCount?: number;
      metadata?: { startTime: number };
    };
    
    // Record failed request for performance monitoring
    const startTime = originalRequest?.metadata?.startTime;
    if (startTime) {
      cachePerformanceMonitor.recordFetchRequest(
        originalRequest?.url || '',
        originalRequest?.method?.toUpperCase() || 'GET',
        startTime,
        false
      );
    }
    
    // Check for SESSION_KICKED error (must be checked before general 401 handling)
    if (isSessionKickedError(error)) {
      // Log session kick detection
      securityAuditLogger.logSecurityEvent('session_kicked_api_error', {
        url: originalRequest?.url,
        method: originalRequest?.method?.toUpperCase(),
        timestamp: new Date().toISOString(),
        message: (error.response?.data as any)?.message,
        details: (error.response?.data as any)?.details
      });
      
      // Stop session monitoring if active
      if (sessionMonitor.isMonitoring()) {
        console.log('[API Client] Stopping session monitor due to SESSION_KICKED error');
        sessionMonitor.stop();
      }
      
      // Enhance error with user-friendly message
      const enhancedError = error as any;
      enhancedError.userMessage = getSessionKickedMessage(error);
      enhancedError.isSessionKicked = true;
      
      // Don't attempt token refresh for SESSION_KICKED errors
      // The error will be caught by session monitor or handled by calling code
      return Promise.reject(enhancedError);
    }
    
    // Handle authentication errors with automatic token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Log security event for authentication failure
      securityAuditLogger.logSecurityEvent('authentication_failure', {
        url: originalRequest?.url,
        method: originalRequest?.method?.toUpperCase(),
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      });
      
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          if (originalRequest && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await tokenManager.refreshToken();
        if (newToken && originalRequest && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          
          // Log successful token refresh
          securityAuditLogger.logSecurityEvent('token_refresh_success', {
            timestamp: new Date().toISOString()
          });
          
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // Log failed token refresh
        securityAuditLogger.logSecurityEvent('token_refresh_failure', {
          timestamp: new Date().toISOString(),
          error: refreshError instanceof Error ? refreshError.message : 'Unknown error'
        });
        
        // Clear tokens and redirect to login
        tokenManager.clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // Retry logic for transient network errors
    if (isRetryableError(error) && originalRequest) {
      const retryCount = originalRequest._retryCount || 0;
      
      if (retryCount < RETRY_CONFIG.MAX_RETRIES) {
        originalRequest._retryCount = retryCount + 1;
        
        const delay = getRetryDelay(retryCount);
        
        console.log(
          `[API Client] Retrying request (${retryCount + 1}/${RETRY_CONFIG.MAX_RETRIES}) after ${delay}ms`,
          {
            url: originalRequest.url,
            method: originalRequest.method,
            error: error.message,
            statusCode: error.response?.status
          }
        );
        
        // Log retry attempt
        securityAuditLogger.logSecurityEvent('api_request_retry', {
          url: originalRequest.url,
          method: originalRequest.method?.toUpperCase(),
          retryCount: retryCount + 1,
          delay,
          error: error.message,
          timestamp: new Date().toISOString()
        });
        
        // Wait before retrying
        await sleep(delay);
        
        // Retry the request
        return apiClient(originalRequest);
      } else {
        console.error(
          `[API Client] Max retries (${RETRY_CONFIG.MAX_RETRIES}) exceeded`,
          {
            url: originalRequest.url,
            method: originalRequest.method,
            error: error.message
          }
        );
        
        // Log max retries exceeded
        securityAuditLogger.logSecurityEvent('api_request_max_retries_exceeded', {
          url: originalRequest.url,
          method: originalRequest.method?.toUpperCase(),
          maxRetries: RETRY_CONFIG.MAX_RETRIES,
          error: error.message,
          timestamp: new Date().toISOString()
        });
      }
    }
    
    // Handle network errors (offline scenarios)
    if (!error.response && error.message === 'Network Error') {
      console.warn('Network error detected, request may be queued for sync');
    }
    
    return Promise.reject(error);
  }
);

/**
 * Make an API request with offline support
 * If offline and the request is queueable, it will be added to the sync queue
 */
export async function apiRequestWithOfflineSupport<T = any>(
  endpoint: string,
  method: QueuedAction['method'],
  data?: any,
  options?: {
    queueable?: boolean;
    actionType?: QueuedAction['type'];
    enableSessionMonitoring?: boolean;
  }
): Promise<T> {
  const { isOnline } = useSyncStore.getState();
  const { queueable = false, actionType, enableSessionMonitoring = false } = options || {};
  
  // Start session monitoring if requested (e.g., for reading creation)
  if (enableSessionMonitoring && !sessionMonitor.isMonitoring()) {
    console.log('[API Client] Starting session monitoring for request:', endpoint);
    sessionMonitor.start();
  }
  
  // If offline and request is queueable, add to queue
  if (!isOnline && queueable && actionType) {
    console.log(`Offline: Queueing ${actionType} request`);
    
    syncService.queueAction(actionType, endpoint, method, data);
    
    // Return a mock response for optimistic UI updates
    return {
      success: true,
      queued: true,
      message: 'Action queued for sync when online'
    } as T;
  }
  
  // Otherwise, make the request normally
  try {
    const response = await apiClient.request({
      method,
      url: endpoint,
      data
    });
    
    // Stop session monitoring on success
    if (enableSessionMonitoring && sessionMonitor.isMonitoring()) {
      console.log('[API Client] Stopping session monitoring after successful request');
      sessionMonitor.stop();
    }
    
    return response as T;
  } catch (error: any) {
    // Stop session monitoring on error
    if (enableSessionMonitoring && sessionMonitor.isMonitoring()) {
      console.log('[API Client] Stopping session monitoring due to error');
      sessionMonitor.stop();
    }
    
    // If network error and queueable, add to queue
    if (error.message === 'Network Error' && queueable && actionType) {
      console.log(`Network error: Queueing ${actionType} request`);
      
      syncService.queueAction(actionType, endpoint, method, data);
      
      // Return a mock response for optimistic UI updates
      return {
        success: true,
        queued: true,
        message: 'Action queued for sync when online'
      } as T;
    }
    
    throw error;
  }
}

/**
 * Conflict resolution strategy
 * Server data always wins in case of conflicts
 */
export function resolveConflict<T>(_localData: T, serverData: T): T {
  console.log('Conflict detected, using server data');
  return serverData;
}
